/**
 * Plays a loud notification sound
 * Uses Web Audio API to generate a prominent notification sound
 * @param {string} soundType - Type of sound: 'bing' (default), 'urgent', 'chime', 'alert'
 */
export const playNotificationSound = (soundType = 'chime') => {
  switch (soundType) {
    case 'urgent':
      return playUrgentSound();
    case 'chime':
      return playChimeSound();
    case 'alert':
      return playAlertSound();
    case 'bing':
    default:
      return playBingSound();
  }
};

/**
 * Plays a loud "bing" notification sound (original)
 */
const playBingSound = () => {
  try {
    // Create audio context
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for the main tone
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Set up the "bing" sound - a bright, attention-grabbing tone
    // Start at a higher frequency and quickly drop for a "bing" effect
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime); // Start at 1000Hz (higher, more prominent)
    oscillator.frequency.exponentialRampToValueAtTime(500, audioContext.currentTime + 0.15); // Drop to 500Hz over longer duration
    
    // Create an envelope for volume - quick attack, sustained, then decay
    const now = audioContext.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(1.0, now + 0.005); // Very quick attack to 100% volume (max loudness)
    gainNode.gain.setValueAtTime(1.0, now + 0.05); // Sustain at max volume
    gainNode.gain.exponentialRampToValueAtTime(0.5, now + 0.1); // Gradual decay to 50%
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3); // Fade out
    
    // Add a second oscillator for richness (harmonic)
    const oscillator2 = audioContext.createOscillator();
    const gainNode2 = audioContext.createGain();
    
    oscillator2.connect(gainNode2);
    gainNode2.connect(audioContext.destination);
    
    oscillator2.type = 'sine';
    oscillator2.frequency.setValueAtTime(1500, audioContext.currentTime); // Higher harmonic
    oscillator2.frequency.exponentialRampToValueAtTime(750, audioContext.currentTime + 0.15);
    
    gainNode2.gain.setValueAtTime(0, now);
    gainNode2.gain.linearRampToValueAtTime(0.6, now + 0.005); // Higher volume for harmonic
    gainNode2.gain.setValueAtTime(0.6, now + 0.05);
    gainNode2.gain.exponentialRampToValueAtTime(0.3, now + 0.1);
    gainNode2.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
    
    // Add a third oscillator for even more presence (low frequency component)
    const oscillator3 = audioContext.createOscillator();
    const gainNode3 = audioContext.createGain();
    
    oscillator3.connect(gainNode3);
    gainNode3.connect(audioContext.destination);
    
    oscillator3.type = 'sine';
    oscillator3.frequency.setValueAtTime(600, audioContext.currentTime); // Lower frequency for body
    oscillator3.frequency.exponentialRampToValueAtTime(300, audioContext.currentTime + 0.15);
    
    gainNode3.gain.setValueAtTime(0, now);
    gainNode3.gain.linearRampToValueAtTime(0.5, now + 0.005);
    gainNode3.gain.setValueAtTime(0.5, now + 0.05);
    gainNode3.gain.exponentialRampToValueAtTime(0.25, now + 0.1);
    gainNode3.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
    
    // Start all oscillators
    oscillator.start(now);
    oscillator2.start(now);
    oscillator3.start(now);
    
    // Stop after the sound completes (longer duration for more presence)
    oscillator.stop(now + 0.35);
    oscillator2.stop(now + 0.35);
    oscillator3.stop(now + 0.35);
    
    // Clean up
    oscillator.onended = () => {
      audioContext.close().catch(() => {
        // Ignore errors when closing context
      });
    };
  } catch (error) {
    console.warn('Failed to play bing sound:', error);
  }
};

/**
 * Plays an urgent/alarm-like notification sound - very attention-grabbing
 * Perfect for "you are late" type notifications
 */
const playUrgentSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    
    // Create a sharp, urgent double-beep pattern
    // First beep - high and sharp
    const osc1 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    osc1.connect(gain1);
    gain1.connect(audioContext.destination);
    
    osc1.type = 'square'; // Square wave for more aggressive sound
    osc1.frequency.setValueAtTime(1200, now);
    osc1.frequency.setValueAtTime(1200, now + 0.08);
    
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(1.0, now + 0.001);
    gain1.gain.setValueAtTime(1.0, now + 0.08);
    gain1.gain.linearRampToValueAtTime(0, now + 0.1);
    
    // Second beep - slightly lower, more urgent
    const osc2 = audioContext.createOscillator();
    const gain2 = audioContext.createGain();
    osc2.connect(gain2);
    gain2.connect(audioContext.destination);
    
    osc2.type = 'square';
    osc2.frequency.setValueAtTime(1000, now + 0.12);
    osc2.frequency.setValueAtTime(1000, now + 0.2);
    
    gain2.gain.setValueAtTime(0, now + 0.12);
    gain2.gain.linearRampToValueAtTime(1.0, now + 0.121);
    gain2.gain.setValueAtTime(1.0, now + 0.2);
    gain2.gain.linearRampToValueAtTime(0, now + 0.22);
    
    // Add a low-frequency rumble for urgency
    const osc3 = audioContext.createOscillator();
    const gain3 = audioContext.createGain();
    osc3.connect(gain3);
    gain3.connect(audioContext.destination);
    
    osc3.type = 'sawtooth';
    osc3.frequency.setValueAtTime(200, now);
    osc3.frequency.setValueAtTime(150, now + 0.22);
    
    gain3.gain.setValueAtTime(0, now);
    gain3.gain.linearRampToValueAtTime(0.4, now + 0.001);
    gain3.gain.setValueAtTime(0.4, now + 0.22);
    gain3.gain.linearRampToValueAtTime(0, now + 0.25);
    
    osc1.start(now);
    osc1.stop(now + 0.1);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.22);
    osc3.start(now);
    osc3.stop(now + 0.25);
    
    osc1.onended = () => {
      audioContext.close().catch(() => {});
    };
  } catch (error) {
    console.warn('Failed to play urgent sound:', error);
  }
};

/**
 * Plays a pleasant chime sound - more gentle but still noticeable
 */
const playChimeSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    
    // Create a pleasant chime with multiple harmonics
    const frequencies = [800, 1000, 1200]; // Musical chord
    
    frequencies.forEach((freq, index) => {
      const osc = audioContext.createOscillator();
      const gain = audioContext.createGain();
      osc.connect(gain);
      gain.connect(audioContext.destination);
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + index * 0.05);
      
      gain.gain.setValueAtTime(0, now + index * 0.05);
      gain.gain.linearRampToValueAtTime(0.7, now + index * 0.05 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.01, now + index * 0.05 + 0.4);
      
      osc.start(now + index * 0.05);
      osc.stop(now + index * 0.05 + 0.4);
    });
  } catch (error) {
    console.warn('Failed to play chime sound:', error);
  }
};

/**
 * Plays an alert sound - like a system alert/notification
 */
const playAlertSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const now = audioContext.currentTime;
    
    // Create a classic alert sound - rising tone
    const osc = audioContext.createOscillator();
    const gain = audioContext.createGain();
    osc.connect(gain);
    gain.connect(audioContext.destination);
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.linearRampToValueAtTime(1200, now + 0.2);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(1.0, now + 0.01);
    gain.gain.setValueAtTime(1.0, now + 0.2);
    gain.gain.linearRampToValueAtTime(0, now + 0.25);
    
    osc.start(now);
    osc.stop(now + 0.25);
    
    osc.onended = () => {
      audioContext.close().catch(() => {});
    };
  } catch (error) {
    console.warn('Failed to play alert sound:', error);
  }
};

